import React, { useEffect, useState } from "react";
import { Lock, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { Skeleton } from "@/components/ui-kit";
import { trackPickSubmitted } from "@/lib/telemetry";

/**
 * User prediction card publisher. Mirrors mobile game-takes: SU (required),
 * ATS + O/U (optional), confidence + reasoning. Server-authoritative kickoff
 * lock; published cards are IMMUTABLE (no edits) — matches canonical rules.
 */
export function MyPick({ game }) {
  const gameId = game.id;
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ su_pick: "", ats_pick: "", ou_pick: "", confidence: 3, reasoning: "" });
  const [busy, setBusy] = useState(false);

  const load = () => TurfTalk.gamePredictions(gameId).then(setData).catch(() => setData({ locked: true }));
  useEffect(() => { load(); }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Skeleton className="h-40" />;

  const mine = data.my_card || data.card;
  const locked = data.locked;
  const home = game.home, away = game.away;

  if (mine) {
    return (
      <div className="tt-panel rounded-xl p-4" data-testid="my-pick-card">
        <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={16} className="text-[#4ea152]" /><span className="font-display tracking-wide text-white text-sm">YOUR PUBLISHED CARD</span><span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/50">IMMUTABLE</span></div>
        <div className="grid grid-cols-3 gap-3 text-sm tabnums">
          <div><div className="text-white/40 text-[11px]">STRAIGHT UP</div><div className="text-white">{mine.su_pick === home.id ? home.abbr : away.abbr}</div></div>
          <div><div className="text-white/40 text-[11px]">ATS</div><div className="text-white">{mine.ats_pick ? mine.ats_pick.toUpperCase() : "—"}</div></div>
          <div><div className="text-white/40 text-[11px]">O/U</div><div className="text-white">{mine.ou_pick ? mine.ou_pick.toUpperCase() : "—"}</div></div>
        </div>
        {mine.reasoning && <p className="text-white/60 text-sm mt-3 italic">“{mine.reasoning}”</p>}
        <div className="mt-3 text-[11px] text-white/40">Grades against the final result. {mine.result ? `Result: ${String(mine.result).toUpperCase()}` : "Awaiting kickoff/grading."}</div>
      </div>
    );
  }

  if (locked) {
    return <div className="tt-panel rounded-xl p-4 flex items-center gap-2 text-white/50 text-sm" data-testid="my-pick-locked"><Lock size={15} /> Predictions are locked for this game — kickoff has passed.</div>;
  }

  const submit = async () => {
    if (!form.su_pick) { toast.error("Pick a straight-up winner."); return; }
    setBusy(true);
    try {
      await TurfTalk.publishCard(gameId, {
        su_pick: form.su_pick,
        ats_pick: form.ats_pick || null,
        ou_pick: form.ou_pick || null,
        confidence: Number(form.confidence),
        reasoning: form.reasoning.trim() || "No notes.",
        proj_home: null, proj_away: null,
      });
      toast.success("Card published (locked & immutable)");
      trackPickSubmitted(gameId);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const Pill = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`px-3 h-9 rounded-lg text-sm border transition-colors ${active ? "bg-[#24362A] border-[#2F7D32] text-white" : "border-[#2A3544] text-white/60 hover:text-white"}`}>{children}</button>
  );

  return (
    <div className="tt-panel rounded-xl p-4" data-testid="my-pick-form">
      <div className="font-display tracking-wide text-white text-sm mb-3">MAKE YOUR PICK</div>
      <div className="mb-3"><div className="text-white/40 text-[11px] mb-1">STRAIGHT-UP WINNER *</div><div className="flex gap-2">
        <Pill active={form.su_pick === away.id} onClick={() => setForm((s) => ({ ...s, su_pick: away.id }))} tid="pick-su-away">{away.abbr}</Pill>
        <Pill active={form.su_pick === home.id} onClick={() => setForm((s) => ({ ...s, su_pick: home.id }))} tid="pick-su-home">{home.abbr}</Pill>
      </div></div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div><div className="text-white/40 text-[11px] mb-1">AGAINST THE SPREAD</div><div className="flex gap-2">
          <Pill active={form.ats_pick === "away"} onClick={() => setForm((s) => ({ ...s, ats_pick: s.ats_pick === "away" ? "" : "away" }))} tid="pick-ats-away">{away.abbr}</Pill>
          <Pill active={form.ats_pick === "home"} onClick={() => setForm((s) => ({ ...s, ats_pick: s.ats_pick === "home" ? "" : "home" }))} tid="pick-ats-home">{home.abbr}</Pill>
        </div></div>
        <div><div className="text-white/40 text-[11px] mb-1">OVER / UNDER</div><div className="flex gap-2">
          <Pill active={form.ou_pick === "over"} onClick={() => setForm((s) => ({ ...s, ou_pick: s.ou_pick === "over" ? "" : "over" }))} tid="pick-ou-over">Over</Pill>
          <Pill active={form.ou_pick === "under"} onClick={() => setForm((s) => ({ ...s, ou_pick: s.ou_pick === "under" ? "" : "under" }))} tid="pick-ou-under">Under</Pill>
        </div></div>
      </div>
      <div className="mb-3"><div className="text-white/40 text-[11px] mb-1">CONFIDENCE ({form.confidence}/5)</div>
        <input type="range" min={1} max={5} value={form.confidence} onChange={(e) => setForm((s) => ({ ...s, confidence: e.target.value }))} className="w-full accent-[#2F7D32]" data-testid="pick-confidence" /></div>
      <textarea value={form.reasoning} onChange={(e) => setForm((s) => ({ ...s, reasoning: e.target.value }))} maxLength={280} placeholder="Reasoning (optional)…" data-testid="pick-reasoning" className="w-full min-h-[70px] bg-[#10151D] border border-[#2A3544] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F7D32] mb-3" />
      <button onClick={submit} disabled={busy} data-testid="pick-publish" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white text-sm font-medium disabled:opacity-50"><Send size={15} /> Publish card (locks at kickoff)</button>
    </div>
  );
}
